<script setup lang="ts">
import { AppText } from '@shared/ui/typography'
import { BtnSecondary } from '@shared/ui/buttons'

import { useAssessmentPreview } from '../../../model/assessments/assessment-preview.hook'
import PreviewScreen from './PreviewScreen.vue'

/**
 * The read-only preview: one screen at a time, with next/previous controls.
 *
 * **It writes nothing** — no store action is called here or in anything below it. It follows the
 * editor's selection by a one-way read; stepping away is a transient override, and the component
 * says so. Functional and unstyled (§A-10): an authoring aid, not the player.
 */
defineOptions({
  name: 'AssessmentPreview',
})

const {
  refScreen,
  refPosition,
  refCount,
  refHasScreens,
  refIsFirst,
  refIsLast,
  refIsFollowingSelection,
  goPrevious,
  goNext,
} = useAssessmentPreview()
</script>

<template>
  <div class="assessment-preview">
    <AppText name="body1-medium">
      Preview
    </AppText>
    <AppText
      name="body2"
      color="slate-500"
    >
      What a kid would see, unanswered. Read-only — nothing here changes the assessment, and
      nothing is graded.
    </AppText>

    <!-- What `create` produces, so it is the first thing most authors meet. -->
    <AppText
      v-if="!refHasScreens"
      name="body2"
      color="slate-500"
    >
      No screens yet. Add a screen in the rail and it will appear here.
    </AppText>

    <template v-else>
      <div class="assessment-preview__stepper">
        <BtnSecondary
          text="Previous"
          :disabled="refIsFirst"
          @click="goPrevious"
        />
        <AppText
          name="body2"
          color="slate-500"
        >
          Screen {{ refPosition }} of {{ refCount }}
        </AppText>
        <BtnSecondary
          text="Next"
          :disabled="refIsLast"
          @click="goNext"
        />
      </div>

      <AppText
        v-if="!refIsFollowingSelection"
        name="body2"
        color="slate-500"
      >
        You are previewing a different screen from the one selected in the rail. Selecting a
        screen brings the preview back to it.
      </AppText>

      <PreviewScreen
        v-if="refScreen"
        :screen="refScreen"
      />
    </template>
  </div>
</template>

<style scoped lang="scss" src="./AssessmentPreview.scss" />
